import React, { useEffect } from 'react';
import Container from '../components/common/Container';
import { Heading } from '../components/common/TextComponents';
import Footer from '../components/layout/Footer';
import Navbar from '../components/layout/Navbar';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed Navbar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <Container>
          <Navbar />
        </Container>
      </div>

      {/* Content with padding to account for fixed navbar */}
      <div className="pt-24">
        {/* Clean header section */}
        <div className="relative py-16 sm:py-24">
          <Container>
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 mb-6">
                Burnt Inc. Privacy Policy
              </h1>
              <p className="text-sm text-gray-500">
                Effective Date: August 5, 2025
              </p>
            </div>
          </Container>
        </div>

        {/* Main content */}
        <div className="pb-16 sm:pb-24">
          <Container>
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg prose-gray max-w-none">
                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">1. Introduction and Scope</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      This Privacy Policy describes how Burnt Inc. (“Burnt,” “we,” “us”) collects, uses, and discloses Personal Data from applicants and members (“Members,” “you”) of the Burnt private professional network (the “Service”). This Policy applies to the Service and all related features. By accessing or using our Service, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your Personal Data as described in this Privacy Policy and our Terms of Use.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">2. Personal Data We Collect</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <h3 className="font-semibold text-gray-900">A. Data You Provide Directly</h3>
                    <ul className="list-disc list-inside space-y-2">
                      <li>
                        <strong>Application Data:</strong> When you apply for membership, we collect your name, contact information, employment history, qualifications, and other professional data contained in your resume or application form.
                      </li>
                      <li>
                        <strong>Profile Data:</strong> Upon becoming a Member, you may provide additional information for your profile, such as a photograph, skills, and career interests.
                      </li>
                      <li>
                        <strong>Community Data:</strong> We collect the content you post in public areas of the Service, such as forums and discussion boards.
                      </li>
                      <li>
                        <strong>Event Data:</strong> When you register for an event, we collect registration information. For in-person events, this may include optional, sensitive data such as dietary restrictions, which we collect with your explicit consent for the sole purpose of event administration.
                      </li>
                      <li>
                        <strong>Communications Data:</strong> We collect data when you contact us for support or other inquiries.
                      </li>
                    </ul>

                    <h3 className="font-semibold text-gray-900 mt-6">B. Data Collected Automatically</h3>
                    <ul className="list-disc list-inside space-y-2">
                      <li>
                        <strong>Usage Data:</strong> We automatically collect technical data about your interactions with the Service, such as your IP address, browser type, pages visited, and timestamps, through log files and other technologies.
                      </li>
                      <li>
                        <strong>Cookies and Similar Technologies:</strong> We use cookies to operate the Service, authenticate you, remember your preferences, and perform analytics.
                      </li>
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">3. How We Disclose Personal Data</h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>We disclose Personal Data in the following limited circumstances:</p>
                    <ul className="list-disc list-inside space-y-2">
                      <li>
                        <strong>To Other Members:</strong> To facilitate networking, your profile data and community posts are visible to other approved Members of the network.
                      </li>
                      <li>
                        <strong>To Employers:</strong> If you provide your explicit consent (opt-in), we will share your profile data with vetted Employer partners.
                      </li>
                      <li>
                        <strong>To Service Providers:</strong> We engage third-party vendors (e.g., cloud hosting, analytics) who are bound by confidentiality obligations to process Personal Data on our behalf.
                      </li>
                      <li>
                        <strong>To Event Partners:</strong> For event administration, we may share necessary logistical information (e.g., attendee lists) with venues or co-sponsors.
                      </li>
                      <li>
                        <strong>For Legal and Safety Reasons:</strong> We may disclose data if we believe in good faith that it is necessary to comply with a legal obligation, protect the safety of any person, or to address fraud, security, or technical issues.
                      </li>
                      <li>
                        <strong>In a Business Transfer:</strong> In the event of a merger, acquisition, or sale of assets, your Personal Data may be transferred to the successor entity.
                      </li>
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">4. Data Security and Retention</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      We implement appropriate technical and organizational measures designed to protect your Personal Data. However, no security system is impenetrable, and we cannot guarantee the security of our systems. We retain your Personal Data for as long as you are a Member or as necessary to fulfill the purposes outlined in this Policy. Data from unsuccessful applications is retained for up to 24 months for reconsideration and compliance purposes, after which it is securely deleted.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">5. Your Privacy Rights</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      Depending on your jurisdiction, you may have rights regarding your Personal Data, including the right to access, correct, delete, or restrict its processing. You may exercise these rights by contacting us. We will respond to your request in accordance with applicable law.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">6. U.S. State-Specific Rights (e.g., California)</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      If you are a resident of a U.S. state with applicable privacy laws (such as California), you may have additional rights, including the Right to Know about the Personal Data we collect and the Right to Delete your Personal Data, subject to certain exceptions. We do not “sell” or “share” Personal Data as those terms are typically defined in these laws. To exercise your rights, please contact us.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">7. International Data Transfers</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      Our Service is hosted in the United States. If you are accessing the Service from outside the U.S., you acknowledge that your Personal Data will be transferred to, stored, and processed in the U.S.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">8. Changes to this Privacy Policy</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>
                      We may modify this Privacy Policy from time to time. We will post any changes on this page and update the “Effective Date” at the top.
                    </p>
                  </div>
                </section>

                <section className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">9. Contact Us</h2>
                  <div className="space-y-6 text-gray-700 leading-relaxed">
                    <p>For any questions or concerns about this Privacy Policy or our data practices, please contact us at:</p>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <address className="text-gray-700 not-italic leading-relaxed">
                        <strong>Burnt Inc.</strong><br />
                        Email: <a href="mailto:support@justburnt.com" className="text-purple-600 hover:text-purple-700 underline">support@justburnt.com</a>
                      </address>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </Container>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default PrivacyPolicy; 